import Swiper from "swiper";
import {
  FreeMode,
  Mousewheel,
  Navigation,
  Pagination,
  Scrollbar,
} from "swiper/modules";
import type { SwiperModule, SwiperOptions } from "swiper/types";

const PRODUCTS_IN_VIEW = 3;
const PRODUCTS_IN_VIEW_LARGE = 4;
// Below the 768 breakpoint, Swiper falls back to this base config. Mobile
// keeps a partial third slide peeking in from the edge as a scroll affordance.
const PRODUCTS_IN_VIEW_MOBILE = 2;
const SPACE_BETWEEN = 20;
const SPACE_BETWEEN_NARROW = 10;

class WearItWith extends HTMLElement {
  private swiper: Swiper | null = null;
  private container: HTMLElement | null = null;

  connectedCallback(): void {
    this.container = this.querySelector<HTMLElement>(".swiper-container");

    if (!this.container) {
      console.warn("Swiper container not found in wear-it-with");
      return;
    }

    this.initSwiper();
  }

  disconnectedCallback(): void {
    this.destroySwiper();
  }

  private buildBreakpoints(): SwiperOptions["breakpoints"] {
    return {
      768: { slidesPerView: PRODUCTS_IN_VIEW, spaceBetween: SPACE_BETWEEN },
      1440: {
        slidesPerView: PRODUCTS_IN_VIEW_LARGE,
        spaceBetween: SPACE_BETWEEN_NARROW,
      },
      3001: {
        slidesPerView: PRODUCTS_IN_VIEW_LARGE,
        spaceBetween: SPACE_BETWEEN,
      },
    };
  }

  private get scope(): HTMLElement {
    return this.closest<HTMLElement>(".shopify-section") ?? this;
  }

  private updateCounter(activeIndex: number): void {
    const display = this.scope.querySelector(".display");

    if (!display || !this.container) {
      return;
    }

    const firstIndex = activeIndex + 1;
    const lastIndex = firstIndex + PRODUCTS_IN_VIEW - 1;
    const total = this.container.querySelectorAll(".swiper-slide").length;

    display.textContent = `${firstIndex} – ${lastIndex} of ${total}`;
  }

  private initSwiper(): void {
    if (this.swiper || !this.container) {
      return;
    }

    const scope = this.scope;
    const scrollbarEl =
      this.container.querySelector<HTMLElement>(".swiper-scrollbar");
    const nextEl = scope.querySelector<HTMLElement>(".slider-button--next");
    const prevEl = scope.querySelector<HTMLElement>(".slider-button--prev");
    const paginationEl = scope.querySelector<HTMLElement>(".slider-pagination");

    const modules: SwiperModule[] = [FreeMode, Mousewheel];

    if (scrollbarEl) {
      modules.push(Scrollbar);
    }

    if (nextEl || prevEl) {
      modules.push(Navigation);
    }

    if (paginationEl) {
      modules.push(Pagination);
    }

    try {
      this.swiper = new Swiper(this.container, {
        modules,
        loop: false,
        freeMode: { enabled: true },
        // When there are fewer products than fit in the viewport, group the
        // slides in the middle instead of leaving them stuck to the start.
        centerInsufficientSlides: true,
        grabCursor: true,
        slidesPerView: PRODUCTS_IN_VIEW_MOBILE,
        spaceBetween: SPACE_BETWEEN_NARROW,
        breakpoints: this.buildBreakpoints(),
        mousewheel: {
          forceToAxis: true,
        },
        ...(scrollbarEl && {
          scrollbar: { el: scrollbarEl, draggable: true },
        }),
        ...((nextEl || prevEl) && {
          navigation: { nextEl, prevEl },
        }),
        ...(paginationEl && {
          pagination: { el: paginationEl, clickable: true },
        }),
        on: {
          init: (swiper) => {
            window.lazySizes?.autoSizer?.checkElems();
            this.updateCounter(swiper.activeIndex);
          },
          activeIndexChange: (swiper) => {
            this.updateCounter(swiper.activeIndex);
          },
        },
      });
    } catch (error) {
      console.error("Error initializing Swiper in wear-it-with:", error);
    }
  }

  private destroySwiper(): void {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }
}

if (!customElements.get("wear-it-with")) {
  customElements.define("wear-it-with", WearItWith);
}

export default WearItWith;
